import { useMemo } from 'react';
import { PASSWORD_RULES, PASSWORD_ERROR_MESSAGES } from '../constants';

/**
 * Hook para validar senha em tempo real
 *
 * @param password - Senha a ser validada
 * @param confirmPassword - Confirmação da senha (opcional)
 * @returns Objeto com erros e status de validação
 *
 * @example
 * ```tsx
 * const { errors, isValid, confirmMatches } = usePasswordValidation(
 *   newPassword,
 *   confirmPassword
 * );
 *
 * if (!isValid) {
 *   return <ErrorList errors={errors} />;
 * }
 * ```
 */
export const usePasswordValidation = (
  password: string,
  confirmPassword?: string
) => {
  const errors = useMemo(() => {
    const validationErrors: string[] = [];

    if (password.length === 0) {
      return validationErrors;
    }

    if (password.length < PASSWORD_RULES.minLen) {
      validationErrors.push(PASSWORD_ERROR_MESSAGES.minLength);
    }

    if (!PASSWORD_RULES.upper.test(password)) {
      validationErrors.push(PASSWORD_ERROR_MESSAGES.uppercase);
    }

    if (!PASSWORD_RULES.lower.test(password)) {
      validationErrors.push(PASSWORD_ERROR_MESSAGES.lowercase);
    }

    if (!PASSWORD_RULES.digit.test(password)) {
      validationErrors.push(PASSWORD_ERROR_MESSAGES.digit);
    }

    if (!PASSWORD_RULES.special.test(password)) {
      validationErrors.push(PASSWORD_ERROR_MESSAGES.special);
    }

    return validationErrors;
  }, [password]);

  const isValid = useMemo(() => {
    return password.length > 0 && errors.length === 0;
  }, [password, errors]);

  const confirmMatches = useMemo(() => {
    if (!confirmPassword || confirmPassword.length === 0) {
      return true;
    }
    return password === confirmPassword;
  }, [password, confirmPassword]);

  const strength = useMemo(() => {
    if (password.length === 0) return 0;

    let score = 0;
    if (password.length >= PASSWORD_RULES.minLen) score++;
    if (PASSWORD_RULES.upper.test(password)) score++;
    if (PASSWORD_RULES.lower.test(password)) score++;
    if (PASSWORD_RULES.digit.test(password)) score++;
    if (PASSWORD_RULES.special.test(password)) score++;

    return (score / 5) * 100;
  }, [password]);

  return {
    errors,
    isValid,
    confirmMatches,
    strength,
  };
};
